import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import PageHeader from "app/main/shared-components/page-header/PageHeader";
import PaginationBar from "app/main/shared-components/pagination-bar/PaginationBar";
import {AppState} from "app/store";
import {clearfilterFlights, filterFlights} from "../store/flights-list/filter-flights.slice";
import {fetchAllCurrencies} from "../store/currencies-list.slice";
import {updateFlight} from "../store/flights-list/update-flight.slice";
import Paper from "@material-ui/core/Paper";
import TableContainer from "@material-ui/core/TableContainer";
import LoadingTableRow from "../../shared-components/base-table/LoadingTableRow";
import NoDataTableRow from "../../shared-components/base-table/NoDataTableRow";
import DataGrid from "react-data-grid";
import {FlightRequestBody} from "../../../types/service-types/FlightTypes";
import {makeStyles} from "@material-ui/core/styles";
import {errorMsg} from "../../../utils/MessageUtils";
import {FlightService} from "../../../services";

type TableRow = {
    fullFlight: FlightRequestBody;
    flightInfo: string;
    freeSeats: string;
    maxNumberOfTravelers: number;
    oneWayAdult: number;
    oneWayChild: number;
    oneWayInfant: number;
    returnAdult: number;
    returnChild: number;
    returnInfant: number;
};

const tableStyles = makeStyles({
    grid: {
        '& .react-grid-Canvas': {overflowY: 'hidden !important'},
    }
})

const FlightsListPriceEdit: React.FC = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = tableStyles();

    const {allFlights, loading} = useSelector(
        (state: AppState) => state.flights.flightsList.allFlights
    );
    const filters = useSelector((state: AppState) => state.flights.flightsList.filterFlights);

    const [rows, setRows] = useState<TableRow[]>([]);

    const columns = [
    { 
        key: "flightInfo", 
        name: t("app.main.schedules.addPassenger.flightInfo"), 
        editable: false, 
        width: 270 
    },
    { 
        key: "freeSeats", 
        name: t("app.main.schedules.addPassenger.freeSeats"),
        editable: false, 
        width: 160 
    },
    { 
        key: "maxNumberOfTravelers", 
        name: t("app.main.schedules.addPassenger.maxNumberOfTravelers"), 
        editable: true, 
        width: 160 
    },
    { 
        key: "oneWayAdult", 
        name: t("app.main.schedules.addPassenger.oneWayAdult"), 
        editable: true 
    },
    { 
        key: "oneWayChild", 
        name: t("app.main.schedules.addPassenger.oneWayChild"), 
        editable: true 
    },
    { 
        key: "oneWayInfant", 
        name: t("app.main.schedules.addPassenger.oneWayInfant"), 
        editable: true 
    },
    { 
        key: "returnAdult", 
        name: t("app.main.schedules.addPassenger.returnAdult"), 
        editable: true 
    },
    { 
        key: "returnChild", 
        name: t("app.main.schedules.addPassenger.returnChild"), 
        editable: true 
    },
    { 
        key: "returnInfant", 
        name: t("app.main.schedules.addPassenger.returnInfant"), 
        editable: true 
    }
    ];


    const freeSeatPercentage = useCallback((allSeats: number, freeSeats: number) => {
        if (allSeats === 0) {
            return 0
        }
        return ((freeSeats / allSeats) * 100).toFixed(2);
    }, []);

    const mapFlightToRow = useCallback(
        (f: FlightRequestBody): TableRow => {
            const sector = f.sectorPrices[0];
            return {
                fullFlight: f,
                flightInfo: `${f.departureAirport.code} => ${f.arrivalAirport.code} | ${f.number} | ${f.dateOfDeparture} ${f.timeOfDeparture}`,
                freeSeats: `${f.numberOfAvailableSeats} - ${freeSeatPercentage(f.maxNumberOfTravelers, f.numberOfAvailableSeats)}%`,
                maxNumberOfTravelers: f.maxNumberOfTravelers,
                oneWayAdult: sector.price.oneWayPrices.forAdults,
                oneWayChild: sector.price.oneWayPrices.forChildren,
                oneWayInfant: sector.price.oneWayPrices.forInfants,
                returnAdult: sector.price.returnPrices.forAdults,
                returnChild: sector.price.returnPrices.forChildren,
                returnInfant: sector.price.returnPrices.forInfants,
            };
        },
        [freeSeatPercentage]
    );

    const onGridRowsUpdated = useCallback(
        ({fromRow, updated}: any) => {
            //validating input here because library doesn't support validation and creating custom cell inputs with validation takes time
            const isInvalid = Object.values(updated).some((val) => {
                const num = Number(val);
                if (isNaN(num)) {
                    return true;
                }
                return num < 0;
            });

            if (isInvalid) {
                dispatch(errorMsg("Only positive numbers are allowed"));
                return;
            }

            const row = rows[fromRow];
            if (!row) {
                return;
            }

            const { fullFlight } = row;

            const updatedValues = { ...updated };

            if (updatedValues.oneWayAdult !== undefined) {
                const value = Number(updatedValues.oneWayAdult);
                updatedValues.returnAdult = value;
                updatedValues.oneWayChild = value;
                updatedValues.returnChild = value;
            }

            const updatedPrices = {
                oneWayPrices: {
                  forAdults: Number(updatedValues.oneWayAdult ?? row.oneWayAdult),
                  forChildren: Number(updatedValues.oneWayChild ?? row.oneWayChild),
                  forInfants: Number(updatedValues.oneWayInfant ?? row.oneWayInfant),
                },
                returnPrices: {
                  forAdults: Number(updatedValues.returnAdult ?? row.returnAdult),
                  forChildren: Number(updatedValues.returnChild ?? row.returnChild),
                  forInfants: Number(updatedValues.returnInfant ?? row.returnInfant),
                },
            };

            const updateNumberOfSectorSeats = Number(updated.maxNumberOfTravelers ?? fullFlight.sectorPrices[0].numberOfSeats);
            const updatedMaxOnline = Number(updated.maxNumberOfTravelers ?? fullFlight.maxOnline);
            const updatedMaxNumberOfTravelers = Number(updated.maxNumberOfTravelers ?? fullFlight.maxNumberOfTravelers);

            const payload = {
                ...fullFlight,
                id: fullFlight.id,
                currencyId: fullFlight.currency?.id,
                departureAirportId: fullFlight.departureAirport?.id,
                departureAirportCode: fullFlight.departureAirport?.code,
                arrivalAirportId: fullFlight.arrivalAirport?.id,
                arrivalAirportCode: fullFlight.arrivalAirport?.code,
                airlineCompanyId: fullFlight.airlineCompany?.id,
                maxOnline: updatedMaxOnline,
                maxNumberOfTravelers: updatedMaxNumberOfTravelers,
                sectorPrices: [{
                    ...fullFlight.sectorPrices[0],
                    prices: updatedPrices,
                    numberOfSeats: updateNumberOfSectorSeats
                }],
            };

            dispatch(
                updateFlight({
                    id: payload.id,
                    body: payload,
                    onSuccess: async () => {
                        const updatedFlight = await FlightService.getFlightById(payload.id);
                        console.log('updateFlight', updatedFlight)
                        setRows((prev) => {
                            const newRows = [...prev];
                            newRows[fromRow] = mapFlightToRow(updatedFlight);
                            return newRows;
                        })
                    },
                    shouldRefetch: false,
                })
            );

        },
        [dispatch, rows, mapFlightToRow]
    );

    useEffect(() => {
        dispatch(clearfilterFlights());
        dispatch(filterFlights({...filters, dayOfWeek: "", inFlight: false}));
        dispatch(fetchAllCurrencies());
    }, [dispatch]);

    useEffect(() => {
        setRows(allFlights?.content.map((flight) => mapFlightToRow(flight)) ?? [])
    }, [allFlights, mapFlightToRow]);

    return (
        <div>
            <PageHeader
                title={t("app.main.flights.priceEdit.editPrices", "Edit flight prices")}
                desc={t(
                    "app.main.flights.priceEdit.desc",
                    "If you want to edit a flight price, click on the cell you want to edit"
                )}
            />
            <TableContainer component={Paper} className={classes.grid}>
                {loading ? (
                    <LoadingTableRow colSpan={6}/>
                ) : !allFlights || allFlights.content.length === 0 ? (
                    <NoDataTableRow colSpan={6}/>
                ) : (
                    <DataGrid
                        columns={columns}
                        rowGetter={(i: number) => rows[i]}
                        rowsCount={rows.length}
                        onGridRowsUpdated={onGridRowsUpdated}
                        enableCellSelect
                        enableDragAndDrop={false}
                        minHeight={rows.length * 35 + 35}
                    />
                )}
            </TableContainer>
            <PaginationBar
                totalPages={allFlights?.totalPages}
                currentPage={(filters.page || 0) + 1}
                onPageChange={(page: number) => dispatch(filterFlights({...filters, page}))}
            />
        </div>
    );
};

export default FlightsListPriceEdit;
